import { CalendarCustomWeek } from '@molecules/CalendarCustomWeek';
import { TimelineItem } from '@molecules/TimelineItem';
import { useGetTemporalEvents } from '@sunrise-ui/api-client';
import type { Event } from '@sunrise-ui/api/events/api';
import { TemporalRelation } from '@sunrise-ui/api/events/api';
import {
  calendarTheme,
  isNative,
  Loader,
  TextReg,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import { createMarkedDates } from '@utils/eventList';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { CalendarProvider, Timeline } from 'react-native-calendars/src';
import type { Event as CalendarEvent } from 'react-native-calendars/src/timeline/EventBlock';
import { Portal } from 'react-native-paper';

type EventWithCommuniful = CalendarEvent & { communifulEvent: Event };

export function EventsCalendarDisplay() {
  const styles = useDynamicStyles(createStyles);
  const router = useRouter();
  const { date } = useLocalSearchParams<{ date?: string }>();

  const { data: events, isLoading } = useGetTemporalEvents({
    temporalRelation: TemporalRelation.Future,
  });

  const minDate = useMemo(() => {
    if (events?.length) {
      const date = DateTime.fromISO(events[0].start_date!);
      if (date.isValid) return date;
    }
    return DateTime.now();
  }, [events]);

  const selectedDate = useMemo(() => {
    const parsedDate = DateTime.fromISO(date ?? '');
    if (parsedDate.isValid) return parsedDate;
    return minDate;
  }, [date, minDate]);

  const markedDates = useMemo(() => createMarkedDates(events), [events]);

  const { timelineEvents, timelineRange } = useMemo(() => {
    if (!events) {
      return {
        timelineEvents: [],
        timelineRange: { start: 0, end: 24 },
      };
    }

    let earliestHour = 24;
    let latestHour = 0;

    const timelineEvents = events
      .filter(
        (event) =>
          selectedDate >= DateTime.fromISO(event.start_date!).startOf('day') &&
          selectedDate <=
            DateTime.fromISO(event.scheduled_end_date!).endOf('day'),
      )
      .map((event) => {
        const startDate = DateTime.fromISO(event.start_date!);
        const endDate = DateTime.fromISO(event.scheduled_end_date!);

        const isStartingToday = startDate.hasSame(selectedDate, 'day');
        const isEndingToday = endDate.hasSame(selectedDate, 'day');

        if (!isStartingToday) earliestHour = 0;
        else if (startDate.hour < earliestHour) earliestHour = startDate.hour;

        if (!isEndingToday) latestHour = 24;
        else if (endDate.hour > latestHour) latestHour = endDate.hour;

        const start = isStartingToday ? startDate : selectedDate.startOf('day');
        const end = isEndingToday ? endDate : selectedDate.endOf('day');

        return {
          id: event.id!.toString(),
          start: start.toFormat('yyyy-MM-dd HH:mm:ss'),
          end: end.toFormat('yyyy-MM-dd HH:mm:ss'),
          title: event.name ?? '',
          color: 'transparent',
          communifulEvent: event,
        } satisfies EventWithCommuniful;
      });

    const timelineRange = {
      start: Math.max(0, earliestHour - 1),
      end: Math.min(24, latestHour + 2),
    };

    return {
      timelineEvents,
      timelineRange,
    };
  }, [events, selectedDate]);

  const onDateChanged = useCallback(
    (newDate: string) => {
      router.setParams({ date: newDate });
    },
    [router],
  );

  if (isLoading) return <Loader />;

  return (
    <Portal.Host>
      <View style={styles.container}>
        <CalendarProvider
          date={selectedDate.toISODate()}
          onDateChanged={onDateChanged}
          showTodayButton
          theme={calendarTheme}
        >
          <CalendarCustomWeek
            selectedDate={selectedDate}
            onDateChanged={onDateChanged}
            markedDates={markedDates}
          />
          {timelineEvents.length === 0 ? (
            <View style={styles.emptyStateContainer}>
              <TextReg style={styles.emptyStateSubtitle}>
                There are no events scheduled for{' '}
                {selectedDate.toFormat('EEEE, MMMM d')}
              </TextReg>
            </View>
          ) : (
            <Timeline
              date={selectedDate.toISODate()}
              events={timelineEvents}
              rightEdgeSpacing={10}
              timelineLeftInset={isNative ? 64 : 70}
              renderEvent={(event) => {
                const communifulEvent = (
                  event as unknown as EventWithCommuniful
                ).communifulEvent;
                return (
                  <TimelineItem
                    title={communifulEvent.name}
                    eventId={communifulEvent.id}
                  />
                );
              }}
              theme={calendarTheme}
              start={Math.min(timelineRange.start, 9)}
              end={Math.max(timelineRange.end, 23)}
              scrollToFirst={true}
              format24h={false}
            />
          )}
        </CalendarProvider>
      </View>
    </Portal.Host>
  );
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
    },
    emptyStateContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 32,
      paddingVertical: 64,
    },
    emptyStateSubtitle: {
      fontSize: 16,
      textAlign: 'center',
    },
  });
